<template>
  <div class="content">
    <!--    Loader-->
    <loading-panel :loading="loading"></loading-panel>
    <div class="row overflow-scroll">
      <div class="col-12">
        <card card-body-classes="table-full-width">
          <h4 slot="header" class="card-title">Past events</h4>

          <filter-bar
            :filters="filterSchema"
            :initial-value="filterValues"
            :result-count="totalRows"
            :per-page="perPage"
            :per-page-options="perPageOptions"
            saved-filters-key="game-instances"
            @change="onFiltersChanged"
            @update:perPage="onPerPageChange"
          ></filter-bar>

          <div class="mt-3" v-if="noDataAvailable === true">
            <div class="font-icon-detail">
              <span style="font-size: 3em">
                <i class="fas fa-exclamation-triangle"></i>
              </span>
              <h4 class="mt-3">No data available.</h4>
            </div>
          </div>

          <!--Medium and large screens-->
          <div v-if="noDataAvailable === false" class="row d-none d-sm-block table-content">
            <div v-if="load" class="col-md-12 col-lg-12 col-sm-12 col-12">
              <b-table  hover :items="gameInstances"
                        :current-page="currentPage"
                        :fields="fields">
                <template v-slot:cell(booking)="row">
                  <div class="row">
                    <div class="col">
                      <span style="font-size: medium">{{row.item.game}}</span>
                    </div>
                  </div>

                  <div class="row">
                    <div class="col">
                      <span>
                        <i class="tim-icons icon-calendar-60 mr-1 text-primary"></i>
                        <span class="mr-2">{{getDate(row.item.booking_date)}}</span>
                        <i class="tim-icons icon-time-alarm mr-1 text-primary"></i>
                        <span>{{getSimpleTime(row.item.booking_start_time)}}</span>
                      </span>
                    </div>
                  </div>

                </template>
                <template v-slot:cell(player_count)="row">
                  <div v-if="row.item.players">
                    <el-popover trigger="hover"
                                placement="bottom">
                      <div>
                        <div class="popover-body">
                          <div v-for="(p, i) in parseJSON(row.item.players)" :key="'C' + i">
                            {{p.first_name}} {{p.last_name}}
                          </div>
                        </div>
                      </div>
                      <base-button class="like btn-link"
                                   type="info"
                                   size="sm"
                                   icon
                                   slot="reference">
                        <span class="text-white">x{{row.item.waiver_count}}</span>
                      </base-button>
                    </el-popover>
                  </div>
                  <div v-else>
                    <base-button class="like btn-link"
                                 type="info"
                                 size="sm"
                                 icon
                                 slot="reference">
                      <span class="text-white">x{{row.item.waiver_count}}</span>
                    </base-button>
                  </div>
                </template>
                <template v-slot:cell(start_time)="row">
                  <span>{{getTime(row.item.start_time)}}</span>
                </template>
                <template v-slot:cell(completed)="row">
                  <i v-if="row.item.completed" class="text-success tim-icons icon-check-2"></i>
                  <i v-if="!row.item.completed" class="text-danger tim-icons icon-simple-remove"></i>
                </template>
                <template v-slot:cell(game_master)="row">
                  <span>{{row.item.gm_first_name}} {{row.item.gm_last_name}}</span>
                </template>
                <template v-slot:cell(total_time)="row">
                  {{getTotalTime(row.item.start_time, row.item.end_time)}}
                </template>
                <template v-slot:cell(photo_email)="row">
                  <badge v-if="row.item.is_emailed_to_group > 0" type="success">Sent x{{row.item.is_emailed_to_group}}</badge>
                  <badge v-if="row.item.is_emailed_to_group === 0" type="warning">Not sent</badge>
                  <badge v-if="row.item.is_emailed_to_group === null" type="info">No photo taken</badge>
                </template>
                <template v-slot:cell(actions)="row">
                  <el-dropdown>
                    <base-button
                      simple
                      type="primary"
                      link
                    > <i style="font-size: medium" class="tim-icons el-icon-more"></i>
                    </base-button>
                    <template #dropdown>
                      <el-dropdown-menu>
                        <a class="dropdown-item mt-1 mb-1" target="_blank" :href="'history/instance?id=' + row.item.id">
                          <i class="tim-icons text-default icon-puzzle-10 mr-2"></i> View event details
                        </a>
                      </el-dropdown-menu>
                    </template>
                  </el-dropdown>
                </template>
              </b-table>
            </div>
          </div>

          <!--Mobile-->
          <div v-if="noDataAvailable === false" class="row d-block d-sm-none table-content">
            <div v-if="load" class="col-md-12 col-lg-12 col-sm-12 col-12">
              <b-table  hover :items="gameInstances"
                        :current-page="currentPage"
                        :fields="mobile_fields">
                <template v-slot:cell(games)="row">
                  <div class="row">
                    <div class="col-6">
                      <span class="text-warning">{{row.item.game}}</span>
                    </div>
                    <div class="col-6">
                      <div class="row">
                        <router-link
                          :to="'history/instance?id=' + row.item.id"
                          custom
                          v-slot="{ href }"
                        >
                          <a :href="href" target="_blank" rel="noopener">
                            View event details
                          </a>
                        </router-link>
                      </div>
                    </div>
                  </div>
                  <div class="row">
                    <div class="col-12">
                      <i class="tim-icons icon-calendar-60 text-warning"></i> {{getDate(row.item.booking_date)}}
                      <i class="tim-icons icon-time-alarm text-warning"></i>&nbsp;{{getSimpleTime(row.item.booking_start_time)}}
                    </div>
                  </div>
                  <div class="row">
                    <div class="col-12">
                      Start time: <span>{{getTime(row.item.start_time)}}</span>
                    </div>
                  </div>
                  <div class="row">
                    <div class="col-12">
                      Total time: <span>{{getTotalTime(row.item.start_time, row.item.end_time)}}</span>
                    </div>
                  </div>
                  <div class="row">
                    <div class="col-12">
                      Completed? <span>{{getCompleted(row.item.completed)}}</span>
                    </div>
                  </div>
                  <div class="row">
                    <div class="col-12">
                      Host: <span>{{row.item.gm_first_name}} {{row.item.gm_last_name}}</span>
                    </div>
                  </div>
                </template>
              </b-table>
            </div>
          </div>

          <b-pagination v-if="noDataAvailable === false"
                        v-on:input="updateGameInstances()"
                        v-model="currentPage"
                        :total-rows="totalRows"
                        :per-page="perPage"
          ></b-pagination>
        </card>
      </div>
    </div>

  </div>
</template>

<script>
import {
  Popover,
  Select,
  Option,
  DatePicker,
  TimeSelect,
  Tooltip,
  Dropdown,
  DropdownMenu,
  DropdownItem
} from 'element-ui';
  import { BaseCheckbox, BaseRadio, Modal } from 'src/components/index';
  import { Auth } from 'aws-amplify'
  import { BFormInput, BTable, BPagination } from 'bootstrap-vue'
  import { API_LOCATION } from '../../../api-config'
  const axios = require('axios')
  import swal from 'sweetalert2';
  import {date, time, simpleTime, formattedDate, dayOfWeek} from "@/plugins/dateFormatter";
  import Badge from "@/components/Badge";
  import _ from "lodash";
  import moment from "moment"

  export default {
    name: "GameInstances",
    components: {
      [DatePicker.name]: DatePicker,
      [Select.name]: Select,
      [Option.name]: Option,
      [TimeSelect.name]: TimeSelect,
      [Popover.name]: Popover,
      [Dropdown.name]: Dropdown,
      [DropdownMenu.name]: DropdownMenu,
      [DropdownItem.name]: DropdownItem,
      BFormInput,
      BTable,
      BPagination,
      BaseCheckbox,
      BaseRadio,
      Modal,
      Tooltip,
      Badge
    },
    data() {
      return {
        loading: false,
        load: false,
        noDataAvailable: false,
        selectedInstance: { uploaded_image: 'https://console.offthecouch.io/img/placeholder.jpg'},
        file: '',
        uploadUrl: '',
        error: '',
        modals: {
          upload_photo: false
        },
        // SavedViewsMenu/FilterBar drives the first fetch via its initial `change`; this guards it.
        didInitialFetch: false,
        // Flat filter values for the shared <filter-bar>. Seeded from the URL in mounted;
        // saved-views precedence is resolved by FilterBar. game = game id.
        filterValues: {
          game: '',
          gameMaster: '',
          date: { mode: 'range', days: '', from: '', to: '' },
        },
        gameInstances: [],
        perPage: 10,
        currentPage: 1,
        totalRows: 0,
        perPageOptions: [5, 10, 25, 50],
        fields: ['booking', 'player_count', 'start_time', 'total_time', 'completed', 'photo_email', { key: 'game_master', label: 'Host' }, 'actions'],
        mobile_fields: ['games'],
        editing: [],
        value: '',
        gameOptions: '',
        selectedGame: '',
        time_format: JSON.parse(localStorage.getItem('group')).time_format,
        date_format: JSON.parse(localStorage.getItem('group')).date_format,
      };
    },
    computed: {
      // Schema for the shared <filter-bar>: event select, booking daterange, host text.
      filterSchema(){
        const gameOptions = Array.isArray(this.gameOptions)
          ? this.gameOptions.map(g => ({ label: g.name, value: g.id }))
          : []
        return [
          { key: 'game', type: 'select', label: 'Event', options: gameOptions, primary: true },
          { key: 'date', type: 'daterange', label: 'Booking date', primary: true },
          { key: 'gameMaster', type: 'text', label: 'Host', placeholder: 'Host name', operatorLabel: 'contains', primary: true },
        ]
      }
    },
    methods: {
      getTotalTime(start, end){
        if(!start || !end) {
          return '-'
        }
        const startDate = moment(start);
        const endDate = moment(end);
        if(!startDate.isValid() || !endDate.isValid()) {
          return '-'
        }
        const durationInMinutes = endDate.diff(startDate, "minutes");
        if(isNaN(durationInMinutes) || durationInMinutes < 0) {
          return '-'
        }
        return durationInMinutes + ' minutes'
      },
      getDayOfWeek(d){
        if(JSON.parse(localStorage.getItem('group')).show_day_of_week){
          return dayOfWeek(d) + ', '
        }
        return ''
      },
      parseJSON(item){
        return JSON.parse(item)
      },
      // Map the FilterBar daterange value {mode,days,from,to} to the backend's to/from
      // request params. Preserves the original serialization (raw yyyy-MM-dd dates).
      computeDateRange(){
        const d = this.filterValues.date || {}
        let to = ''
        let from = ''
        if (d.mode === 'days' && d.days !== '' && d.days != null) {
          let today = new Date()
          to = today.getFullYear() + '-' + (today.getMonth() + 1) + '-' + today.getDate()
          let dt = new Date()
          dt.setDate(dt.getDate() - Number(d.days))
          from = dt.getFullYear() + '-' + (dt.getMonth() + 1) + '-' + dt.getDate()
        } else if (d.mode === 'range' && d.from && d.to) {
          to = d.to
          from = d.from
        }
        return { to, from }
      },
      async updateGameInstances(source){
        if(source === 'filter'){
          this.currentPage = 1
        }
        const { to, from } = this.computeDateRange()
        let count = this.perPage
        let offset = parseInt((this.currentPage * this.perPage) - this.perPage)
        await this.getGameInstances(count, offset, this.filterValues.game || '', this.filterValues.gameMaster || '', to, from)
      },
      // FilterBar change: apply new values and (re)fetch. The first change is
      // FilterBar's initial saved-views resolution and drives the first load.
      onFiltersChanged(values){
        this.filterValues = {
          game: values.game || '',
          gameMaster: values.gameMaster || '',
          date: { ...(values.date || { mode: 'range', days: '', from: '', to: '' }) },
        }
        if(!this.didInitialFetch){
          this.didInitialFetch = true
          // Initial load preserves the URL page.
          this.updateGameInstances()
        } else {
          // Applying a filter/view lands on page 1; 'filter' resets currentPage before
          // the offset is built (stale page => bad offset => backend error).
          this.updateGameInstances('filter')
        }
      },
      onPerPageChange(n){
        this.perPage = n
        this.currentPage = 1
        this.updateGameInstances('filter')
      },
      getAuthToken() {
        return new Promise ( function(resolve) {
          Auth.currentSession()
            .then(data =>{
              resolve(data.idToken.jwtToken)
            })
            .catch(err => console.log(err))
        })
      },
      getCompleted(c){
        if (c == 0){
          return 'No'
        } else {
          return 'Yes'
        }
      },
      getDate(d){
        return this.getDayOfWeek(d) + formattedDate(this.date_format, d)
      },
      getTime(d){
        if(!d || d === null) {
          return '-'
        }
        if(d[0] == 0 || !d){
          return '-'
        }
        return time(this.time_format, d)
      },
      getSimpleTime(d){
        if(!d || d === null) {
          return '-'
        }
        return time(this.time_format, d)
      },
      async getUsersForBooking(row){
        let token = await this.getAuthToken()
        let config = {headers: {Authorization: token}}
        let companyId = localStorage.getItem('companyId')
        let companyGroupId = JSON.parse(localStorage.getItem('group')).id
        let url = API_LOCATION + 'companies/' + companyId + '/groups/'
          + companyGroupId + '/bookings/' + row.booking_slot_id + '/waivers'

        axios.get(url, config)
          .then(response => {
            row.waiver_count = response.data.users.length
            row.players = JSON.stringify(response.data.users)
          }).catch((err) => {
          console.log(err)
          this.loading = false
          if(err.response.status == 401){
            swal({
              title: 'Error',
              html: 'You do not have access to this feature. Reach out to your admin regarding your site permissions.',
              type: 'error',
            }).then((response)=> {
              this.$router.push('/')
            })
          }
          else if(err.response.status == 500){
            swal({
              title: 'Error',
              html: 'The feature has not been enabled as part of your subscription plan.',
              type: 'error',
            }).then((response)=> {
              this.$router.push('/')
            })
          }
          else {
            swal('Error!', 'Something went wrong', 'error')
          }
        })
      },
      async getGames () {
        var token = await this.getAuthToken()
        let config = { headers: { Authorization: token } }
        let companyId = localStorage.getItem('companyId')
        let companyGroupId = JSON.parse(localStorage.getItem('group')).id
        let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/games'
        axios.get(url, config)
          .then(response => {
            this.gameOptions = response.data
          })
          .catch(err => {
            this.loading = false
            console.log(err)
            if(err.response.status == 401){
              this.$router.push('/forbidden')
            } else {
              swal('Error!', 'Something went wrong', 'error')
            }
          })
      },
      async getGameInstances (count, offset, game, game_master, to, from) {

        if(game_master === undefined){
          game_master = ''
        }
        if(game === undefined){
          game = ''
        }
        if(to === undefined){
          to = ''
        }
        if(from === undefined){
          from = ''
        }

        var token = await this.getAuthToken()
        let config = { headers: { Authorization: token } }
        let companyId = localStorage.getItem('companyId')
        let companyGroupId = JSON.parse(localStorage.getItem('group')).id
        let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/game-instances?count=' + count + "&offset=" + offset + "&game=" + encodeURIComponent(game) +
                  "&game_master=" + encodeURIComponent(game_master) + "&to=" + to + "&from=" + from
        this.load = false
        await this.getGameInstanceURL(url, config)
        this.load = true
      },
      getGameInstanceURL(url, config){
        this.loading = true
        axios.get(url, config)
          .then(response => {
            this.loading = false
            if(this.gameInstances.length <=0){
              this.currentPage = Number(this.$route.query.page)
            }
            this.gameInstances = response.data.gameInstances
            if(this.gameInstances.length <= 0) {
              this.noDataAvailable = true
            } else {
              this.noDataAvailable = false
            }
            for(let n in this.gameInstances) {
              this.getUsersForBooking(this.gameInstances[n])
            }
            this.totalRows = response.data.totalRows
            this.addQueryParameters()

          })
          .catch(err => {
            console.log(err)
            this.loading = false
            if(err.response.status == 401){
              swal({
                title: 'Error',
                html: 'You do not have access to this feature. Reach out to your admin regarding your site permissions.',
                type: 'error',
              }).then((response)=> {
                this.$router.push('/')
              })
            }
            else if(err.response.status == 500){
              swal({
                title: 'Error',
                html: 'The feature has not been enabled as part of your subscription plan.',
                type: 'error',
              }).then((response)=> {
                this.$router.push('/')
              })
            }
            else {
              swal('Error!', 'Something went wrong', 'error')
            }
          })


      },
      async savePhoto(){
        this.loading = true
        await this.saveFile()
        var token = await this.getAuthToken()
        let config = {headers: {Authorization: token}}
        let companyId = localStorage.getItem('companyId')
        let gameInstanceId = this.selectedGame.id
        let url = API_LOCATION + 'companies/' + companyId + '/games/instances/' + gameInstanceId + '/photo-upload'

        let data = { uploaded_image:  this.uploadUrl.split("?")[0]}

        axios.post(url, data, config)
          .then(response => {
            this.loading = false
            this.modals.upload_photo = false
            this.selectedGame = ''
            this.file = ''
            this.uploadUrl = ''
            swal({
              title: 'Photo uploaded!',
              text: 'Go to game details to view photo.',
              type: 'success',
              confirmButtonClass: 'btn btn-success btn-fill',
              confirmButtonText: 'Ok',
              buttonsStyling: false
            })
          }).catch((err) => {
            console.log(err)
            this.loading = false
            if(err.response.status == 401){
              this.$router.push('/forbidden')
            } else {
              swal({
                title: 'Something went wrong!',
                type: 'error',
                confirmButtonClass: 'btn btn-danger btn-fill',
                confirmButtonText: 'Try again',
                buttonsStyling: false
              })
            }

        })
      },
      getOffset(){
        return Number((this.currentPage * this.perPage) - this.perPage)
      },
      addQueryParameters() {
        this.$nextTick(() => {
          this.currentPage = this.$route.query.page
        });

        const d = this.filterValues.date || {}
        const newQuery = {
          ...this.$route.query,
          count: this.perPage,
          page: this.currentPage,
          game_master: this.filterValues.gameMaster || '',
          game: this.filterValues.game || '',
          to: d.to || '',
          from: d.from || '',
        }
        this.$router.replace({query: newQuery}).catch(err => {
          if (err.name !== 'NavigationDuplicated') {
            throw err;
          }
        });
      }
    },
    mounted() {
      // Default when the query omits count/page (e.g. arriving from a saved view with
      // only ?view=), otherwise Number(undefined) => NaN builds a bad fetch URL.
      this.perPage = Number(this.$route.query.count) || 10
      this.currentPage = Number(this.$route.query.page) || 1
      this.loading = true
      // Seed FilterBar's flat values from the URL (game = id). FilterBar resolves
      // saved-views precedence (?view= > URL > default > last-used) and drives the
      // first fetch via its initial `change` (onFiltersChanged).
      const q = this.$route.query || {}
      this.filterValues = {
        game: q.game ? Number(q.game) : '',
        gameMaster: q.game_master || '',
        date: { mode: 'range', days: '', from: q.from || '', to: q.to || '' },
      }
      this.getGames()
    }
  };
</script>
<style scoped>
  .pagination-select{
    width: 100px;
  }
  .table-content {
    overflow-x: scroll;
  }
  .pop {
    max-width: 600px;
  }
  #loading {
    min-height: 1000px;
    display: flex;
    align-items: center;
  }

  .el-loading-spinner .path {
    stroke: #6aff8d !important;
  }

  .el-loading-mask {
    background: rgba(0, 0, 0, 0.7) !important;
  }
</style>

<style>
.white-content .gi-filter-reset {
  color: #344675 !important;
}

.white-content .gi-filter-text {
  color: #344675 !important;
}
</style>
